import React from 'react'
import { translation } from '../translation'

export const uploadAreaStyles: {
  base: React.CSSProperties
  focused: React.CSSProperties
  accept: React.CSSProperties
  rejected: React.CSSProperties
} = {
  base: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    cursor: 'pointer',
    padding: 16,
    borderRadius: 4,
    color: 'var(--white)',
    outline: 'none',
    border: '1px solid var(--inputs)',
    borderColor: 'var(--inputs)',
    transition: 'border .24s ease-in-out',
    height: 260
  },
  focused: {
    borderColor: 'var(--primary)'
  },
  accept: {
    borderColor: 'var(--primary)'
  },
  rejected: {
    borderColor: '#ff1744'
  }
}

export const uploadAreaStylesMinified: {
  base: React.CSSProperties
  focused: React.CSSProperties
  accept: React.CSSProperties
  rejected: React.CSSProperties
} = {
  base: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    cursor: 'pointer',
    padding: 0,
    borderRadius: 4,
    color: 'var(--white)',
    transition: 'border .24s ease-in-out',
    height: 'fit-content'
  },
  focused: {
    borderColor: 'var(--primary)'
  },
  accept: {
    borderColor: 'var(--primary)'
  },
  rejected: {
    borderColor: '#ff1744'
  }
}

export const megabytesToBytes = (megabytes: number) => {
  const bytesInMegabyte = 1024 * 1024
  return megabytes * bytesInMegabyte
}

const extensionsByTypeInterface = {
  video: {
    'video/*': ['.mp4', '.mkv', '.avi', '.mov', '.wmv']
  },
  img: {
    'image/png': ['.png'],
    'image/jpg': ['.jpg'],
    'image/jpeg': ['.jpeg'],
    'image/gif': ['.gif']
  },
  doc: {
    'application/pdf': ['.pdf'],
    'file/doc': ['.doc'],
    'file/docx': ['.docx']
  },
  spreadsheet: {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
      '.xlsx'
    ],
    'application/vnd.ms-excel': ['.xls'],
    'text/csv': ['.csv']
  },
  certificate: {
    'application/x-pkcs12': ['.pfx']
  }
}

export const generateExtensionColors = (ext: string) => {
  const groupPurple = ['video']
  const groupBlue = ['img']
  const groupRed = ['doc', 'docx']
  const groupGreen = ['spreadsheet']

  if (groupPurple.includes(ext)) return '#226EE0'
  if (groupBlue.includes(ext)) return '#36A9E1'
  if (groupRed.includes(ext)) return '#F24D41'
  if (groupGreen.includes(ext)) return '#67CA37'
  return '#F57621'
}

export const getExtensionsByType = (
  type: 'doc' | 'img' | 'video' | 'spreadsheet' | 'mix' | 'certificate'
) => {
  switch (type) {
    case 'video':
      return extensionsByTypeInterface.video
    case 'img':
      return extensionsByTypeInterface.img
    case 'doc':
      return {
        ...extensionsByTypeInterface.img,
        ...extensionsByTypeInterface.doc
      }
    case 'spreadsheet':
      return extensionsByTypeInterface.spreadsheet
    case 'certificate':
      return extensionsByTypeInterface.certificate
    case 'mix':
      return {
        ...extensionsByTypeInterface.video,
        ...extensionsByTypeInterface.img,
        ...extensionsByTypeInterface.doc,
        ...extensionsByTypeInterface.spreadsheet,
        ...extensionsByTypeInterface.certificate
      }
  }
}

export const translateUploadError = (
  type:
    | 'file-invalid-type'
    | 'file-too-large'
    | 'file-too-small'
    | 'too-many-files'
    | string,
  props: {
    type: 'doc' | 'img' | 'video' | 'spreadsheet' | 'mix' | 'certificate'
    maxSize: number // Number of megabytes
    maxQuantity: number // Max. number of files
    description?: string // Description shown on upload area
  },
  language: 'pt' | 'en' | 'es'
) => {
  const strings = translation[language]
  switch (type) {
    case 'file-invalid-type': {
      let base = strings.errorType
      switch (props.type) {
        case 'doc':
          base += strings.errorDoc
          break
        case 'img':
          base += strings.errorImage
          break
        case 'video':
          base += strings.errorVideo
          break
        case 'spreadsheet':
          base += strings.errorSpreadsheet
      }
      return base
    }
    case 'file-too-large': {
      return `${strings.errorSize}${
        props.maxSize > 1000 ? props.maxSize / 1000 : props.maxSize
      }${props.maxSize > 1000 ? 'GB' : 'MB'}`
    }
    case 'file-too-small': {
      return strings.errorSmall
    }
    case 'too-many-files': {
      return `${strings.errorQuantity} ${props.maxQuantity} ${strings.errorFiles}`
    }
    case 'file-exists': {
      return strings.errorExists
    }
  }
}

export const getFileExtension = (file: File | FileWithPreview) => {
  if (file.name) {
    let fileName = file.name
    const questionMarkIndex = fileName.indexOf('?')
    if (questionMarkIndex !== -1) {
      fileName = fileName.substring(0, questionMarkIndex)
    }
    const parts = fileName.split('.')
    if (parts.length > 1) {
      return parts[parts.length - 1]
    }
  }
  return ''
}

export const getUrlFileExtension = (url: string) => {
  const match = url.match(/\.([a-z0-9]+)(?:[?#]|$)/i)
  if (match) {
    return match[1]
  }
  return ''
}

export const removeFileExtension = (file: File | FileWithPreview) => {
  const fileName = file.name
  const parts = fileName.split('.')
  if (parts.length > 1) {
    parts.pop()
    return parts.join('.')
  }
  return fileName
}

export const getFileNameFromUrl = (url: string) => {
  const variablePart = url.match(/\/([a-f0-9-]+)-/)

  if (variablePart && variablePart[1]) {
    const fileNameWithVariable = url.substring(
      (variablePart.index || 0) + variablePart[0].length
    )
    const decodedFileName = decodeURIComponent(fileNameWithVariable)
    const fileName = decodedFileName.split('?')[0]
    return fileName
  }
  return ''
}

export type FileWithPreview = File & { preview: string; duration?: number }

export interface UploadAreaResponse {
  newFiles: string[]
  deletedFiles: string[]
  finalFiles: string[] // Final version of files array to send to API
}

export const mergeFilesAndPreviews = (
  filesArr: FileWithPreview[],
  previews: string[] | string | null
) => {
  const filesArrSanitized = filesArr.map(file => ({
    name: file.name,
    preview: file.preview,
    extension: getFileExtension(file),
    duration: file.duration
  }))
  const previewsSanitized: {
    name: string
    preview: string
    extension: string
    duration?: string
  }[] = []

  if (typeof previews === 'string') {
    previewsSanitized.push({
      name: getFileNameFromUrl(previews),
      preview: previews,
      extension: getUrlFileExtension(previews)
    })
  } else if (Array.isArray(previews)) {
    previewsSanitized.push(
      ...previews.map(preview => ({
        name: getFileNameFromUrl(preview),
        preview: preview,
        extension: getUrlFileExtension(preview)
      }))
    )
  }

  return [...previewsSanitized, ...filesArrSanitized]
}

export const getExtensionsList = (
  type: keyof typeof extensionsByTypeInterface
) => {
  const typeObject = extensionsByTypeInterface[type]
  return Object.values(typeObject)
    .flat()
    .map(ext => ext.replace('.', '').toUpperCase())
}
